import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getDadosCliente } from "@/services/mockDB";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, FileText, Plane, Hotel, Plus, Car, Bus, CheckCircle, Wallet, HelpCircle, Sprout, Search, Lock, Eye, RotateCcw, RefreshCw, X, AlertTriangle, Clock, XCircle, Info, User, Calendar, Mail, Phone, MapPin, Home, IdCard, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";

const ClientDetail: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [checkInForm, setCheckInForm] = useState({
    cidadeOrigem: "",
    meioTransporte: "",
    distancia: ""
  });
  const [showDadosCadastrais, setShowDadosCadastrais] = useState(false); // Inicia fechado para economizar espaço

  // Detectar se veio de uma empresa de hotelaria
  const isHotelaria = location.state?.empresaTipo === "Hotelaria" || 
                      location.state?.companyId === "hotel-mar-brasil";

  // Mock data - em produção viria de uma API baseado no clientId
  const clientsData: Record<string, any> = {
    "1": {
      id: "1",
      nomeCompleto: "João Silva",
      cpf: "123.456.789-00",
      rg: "12.345.678-X",
      dataNascimento: "20/05/1990",
      status: "Ativo",
      email: "joao.silva@email.com",
      telefone: "(11) 98765-4321",
      cep: "01310-100",
      endereco: "Av. Paulista",
      numero: "1578",
      complemento: "Apto 42 - Bloco C",
      bairro: "Bela Vista",
      cidade: "São Paulo",
      uf: "SP",
      pais: "Brasil",
      dataCadastro: "15/01/2024",
      viagens: [
        {
          id: "1",
          data: "20/12/2025",
          horaInicio: "14:30",
          horaFim: "16:15",
          rota: "SDU - GRU",
          distancia: "366 km",
          classe: "Executiva",
          aeronave: "Airbus A320",
          tipoAeronave: "Airbus A320",
          assentos: 180,
          conexoes: 0,
          saf: true,
          safPercentual: "25%",
          emissao: "60 kg",
          valorPassagem: 500.00,
          taxaCashback: 4.0,
          cashbackTotal: 20.00,
          fundoMudas: 0.00,
          projetosSociais: 0.00,
          saldoCarteira: 20.00
        }
      ],
      checkIns: [
        {
          id: "1",
          data: "05/03/2024",
          origem: "Brasília, DF",
          transporte: "Aéreo Convencional",
          distancia: 1060,
          emissao: 265.0,
          moedas: 2650.0,
          status: "Compensado"
        },
        {
          id: "2",
          data: "18/03/2024",
          origem: "Curitiba, PR",
          transporte: "Carro",
          distancia: 410,
          emissao: 77.9,
          moedas: 779.0,
          status: "Compensado"
        },
        {
          id: "3",
          data: "28/03/2024",
          origem: "Porto Alegre, RS",
          transporte: "Ônibus",
          distancia: 1550,
          emissao: 77.5,
          moedas: 775.0,
          status: "Compensado"
        }
      ]
    }
  };

  // Função para calcular idade a partir da data de nascimento
  const calcularIdade = (dataNascimento: string): number => {
    if (!dataNascimento) return 0;
    const [dia, mes, ano] = dataNascimento.split('/').map(Number);
    const hoje = new Date();
    const nascimento = new Date(ano, mes - 1, dia);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mesAtual = hoje.getMonth();
    const diaAtual = hoje.getDate();
    
    if (mesAtual < mes - 1 || (mesAtual === mes - 1 && diaAtual < dia)) {
      idade--;
    }
    
    return idade;
  };

  const clientData = clientsData[clientId || "1"] || clientsData["1"];
  const idade = clientData.dataNascimento ? calcularIdade(clientData.dataNascimento) : 0;
  
  const client = {
    id: clientData.id,
    nomeCompleto: clientData.nomeCompleto,
    cpf: clientData.cpf,
    rg: clientData.rg || "",
    dataNascimento: clientData.dataNascimento || "",
    idade: idade,
    status: clientData.status || "Ativo",
    email: clientData.email,
    telefone: clientData.telefone,
    cep: clientData.cep || "",
    endereco: clientData.endereco || "",
    numero: clientData.numero || "",
    complemento: clientData.complemento || "",
    bairro: clientData.bairro || "",
    cidade: clientData.cidade,
    uf: clientData.uf || "",
    pais: clientData.pais,
    dataCadastro: clientData.dataCadastro
  };

  // Estados para dados do mockDB (sincronizados)
  const [dadosMockDB, setDadosMockDB] = useState<any>(null);
  const [saldoVinculado, setSaldoVinculado] = useState(20.00);
  const [fundoMudas, setFundoMudas] = useState(0.00);
  const [projetosSociais, setProjetosSociais] = useState(0.00);
  const [statusRepasse, setStatusRepasse] = useState<'pendente' | 'parcial' | 'concluido'>('pendente');
  const [saldoLiquido, setSaldoLiquido] = useState(0.00);
  
  // Estados com polling agressivo (força bruta para garantir atualização)
  const [saldoReal, setSaldoReal] = useState(0);
  const [mudasReais, setMudasReais] = useState(0);
  
  const [transacoes, setTransacoes] = useState<Array<{
    id: string;
    dataHora: string;
    origem: string;
    tipo: string;
    destino: string;
    valor: number;
    tipoTransacao: "entrada" | "saida";
  }>>([]);
  const [filtroExtrato, setFiltroExtrato] = useState<"todos" | "entradas" | "saidas">("todos");

  // POLLING AGRESSIVO - Força bruta para garantir atualização em tempo real
  useEffect(() => {
    const recalcularTudo = () => {
      try {
        // 1. Pega os dados do localStorage (mesma chave do mockDB)
        const storageKey = 'banclima_mock_db';
        const dadosCompletos = JSON.parse(localStorage.getItem(storageKey) || '{}');
        const clientIdAtual = clientId || "1";
        const dados = dadosCompletos[clientIdAtual];
        
        if (!dados || !dados.transacoes) {
          // Fallback: tenta via getDadosCliente
          const dadosFallback = getDadosCliente(clientIdAtual);
          if (dadosFallback && dadosFallback.transacoes) {
            setTransacoes(dadosFallback.transacoes);
            setDadosMockDB(dadosFallback);
            setSaldoVinculado(dadosFallback.saldoVinculado || 20.00);
            setStatusRepasse(dadosFallback.destinacaoConfirmada ? 'concluido' : 'pendente');
          }
          return;
        }

        // Atualiza dados do mockDB
        setDadosMockDB(dados);
        setSaldoVinculado(dados.saldoVinculado || 20.00);
        setStatusRepasse(dados.destinacaoConfirmada ? 'concluido' : 'pendente');
        
        // 2. Pega TODAS as transações
        const transacoesSalvas = dados.transacoes || [];
        setTransacoes(transacoesSalvas);

        // 3. LÓGICA FINANCEIRA (R$ 10,00 Inicial - Gastos)
        const gastos = transacoesSalvas
          .filter((t: any) => t.origem === 'Saldo Livre' && t.tipoTransacao === 'saida')
          .reduce((acc: number, curr: any) => acc + Number(Math.abs(curr.valor || 0)), 0);
        
        const saldoInicial = dados.destinacaoConfirmada ? 10.00 : 0.00;
        const saldoFinal = saldoInicial - gastos;
        setSaldoReal(saldoFinal); // Atualiza State
        setSaldoLiquido(saldoFinal); // Mantém compatibilidade

        // 4. LÓGICA DE MUDAS (Regra dos R$ 6,00)
        // Soma todas as transações de "Apoio a Mudas"
        const totalInvestidoMudas = transacoesSalvas
          .filter((t: any) => t.tipo?.includes('Mudas') || t.tipo === 'Apoio a Mudas')
          .reduce((acc: number, curr: any) => acc + Number(Math.abs(curr.valor || 0)), 0);
        
        // Considera também os R$ 5,00 iniciais do voo se a destinação foi confirmada
        // (os R$ 5,00 iniciais já estão nas transações, mas garantimos que estejam)
        const totalGeralMudas = totalInvestidoMudas; // Já inclui os R$ 5,00 iniciais se confirmado
        const qtdMudas = Math.floor(totalGeralMudas / 6.00);
        
        setMudasReais(qtdMudas); // Atualiza State
        setFundoMudas(totalGeralMudas); // Mantém compatibilidade

        // 5. Total Investido em Projetos Sociais
        const totalSocialCalculado = transacoesSalvas
          .filter((t: any) => t.tipo === 'Projetos Sociais' && t.tipoTransacao === 'saida')
          .reduce((acc: number, curr: any) => acc + Number(curr.valor || 0), 0);
        setProjetosSociais(totalSocialCalculado);

        // DEBUG
        console.log('[Admin] Polling - Valores recalculados:', {
          saldoFinal,
          qtdMudas,
          totalGeralMudas,
          gastos,
          transacoesCount: transacoesSalvas.length
        });
      } catch (error) {
        console.error('[Admin] Erro no polling:', error);
      }
    };

    // Roda a cada 500ms (Polling para garantir sincronia entre abas)
    const intervalo = setInterval(recalcularTudo, 500);
    recalcularTudo(); // Roda imediatamente também

    // Listener para evento customizado (mesma aba)
    const handleMockDBUpdate = () => recalcularTudo();
    window.addEventListener('mockDB:updated', handleMockDBUpdate);
    
    // Listener para evento storage (outras abas)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'banclima_mock_db') {
        recalcularTudo();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      clearInterval(intervalo);
      window.removeEventListener('mockDB:updated', handleMockDBUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, [clientId]);

  // Filtrar transações baseado na aba selecionada
  let transacoesFiltradas = transacoes;
  if (filtroExtrato === "entradas") {
    transacoesFiltradas = transacoes.filter(t => t.tipoTransacao === "entrada");
  } else if (filtroExtrato === "saidas") {
    transacoesFiltradas = transacoes.filter(t => t.tipoTransacao === "saida");
  }
  
  // Ordenar por data (mais recente no topo)
  transacoesFiltradas = [...transacoesFiltradas].sort((a, b) => {
    // Converte formato "DD/MM/YYYY HH:MM" para Date
    const parseDate = (dateStr: string) => {
      const [datePart, timePart] = dateStr.split(' ');
      const [day, month, year] = datePart.split('/');
      const [hour, minute] = timePart ? timePart.split(':') : ['00', '00'];
      return new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
    };
    
    try {
      const dateA = parseDate(a.dataHora);
      const dateB = parseDate(b.dataHora);
      return dateB.getTime() - dateA.getTime();
    } catch {
      // Se houver erro na conversão, mantém ordem original
      return 0;
    }
  });

  const viagens = clientData.viagens || [];
  const checkIns = clientData.checkIns || [];

  // Calcular métricas para os cards de resumo
  const totalViagens = viagens.length;
  const totalDistancia = viagens.reduce((sum: number, v: any) => {
    if (!v.distancia) return sum;
    // Remove " km" e pontos de milhar, mantém apenas números
    const distanciaStr = v.distancia.replace(" km", "").replace(/\./g, "").trim();
    return sum + parseFloat(distanciaStr) || 0;
  }, 0);
  const totalEmissao = viagens.reduce((sum: number, v: any) => {
    const emissaoStr = v.emissao?.replace(" kg", "").replace(",", ".").trim() || "0";
    return sum + parseFloat(emissaoStr) || 0;
  }, 0);
  // Usa os valores do polling agressivo (força bruta)
  const totalMudas = mudasReais;
  const saldoCarteira = saldoReal;

  // Dicionário de mapeamento de códigos IATA para nomes completos
  const aeroportos: Record<string, { nome: string; cidade: string; estado?: string }> = {
    "GRU": { nome: "Aeroporto Int. de Guarulhos", cidade: "São Paulo", estado: "SP" },
    "REC": { nome: "Aeroporto Int. dos Guararapes", cidade: "Recife", estado: "PE" },
    "SDU": { nome: "Aeroporto Santos Dumont", cidade: "Rio de Janeiro", estado: "RJ" }
  };

  // Função para gerar tooltip da rota
  const gerarTooltipRota = (rota: string): string => {
    const partes = rota.split(" - ");
    if (partes.length !== 2) return rota;
    
    const origem = partes[0].trim();
    const destino = partes[1].trim();
    
    const infoOrigem = aeroportos[origem];
    const infoDestino = aeroportos[destino];
    
    if (!infoOrigem || !infoDestino) return rota;
    
    return `Origem: ${infoOrigem.cidade}${infoOrigem.estado ? ` (${infoOrigem.estado})` : ''} - ${infoOrigem.nome}\nDestino: ${infoDestino.cidade}${infoDestino.estado ? ` (${infoDestino.estado})` : ''} - ${infoDestino.nome}`;
  };

  // Função para extrair apenas os códigos da rota (sem aeronave)
  const extrairCodigosRota = (rota: string): string => {
    // Remove qualquer texto entre parênteses (aeronave)
    return rota.replace(/\s*\([^)]*\)\s*$/, "").trim();
  };

  // Função para calcular emissão de CO2
  const calcularEmissaoCO2 = (distancia: number, meioTransporte: string): number => {
    const fatoresEmissao: Record<string, number> = {
      "Carro": 0.19,
      "Ônibus": 0.05,
      "Aéreo Convencional": 0.25
    };
    return distancia * (fatoresEmissao[meioTransporte] || 0);
  };

  // Função para calcular moedas
  const calcularMoedas = (emissaoCO2: number): number => {
    return emissaoCO2 * 10; // 10 Moedas por kg de CO2
  };

  // Função para calcular cashback baseado na emissão de CO2 (50 MSC por kg)
  const calcularCashback = (emissaoStr: string): number => {
    // Remove "kg" e converte vírgula para ponto
    const emissaoNum = parseFloat(emissaoStr.replace(" kg", "").replace(",", ".").trim());
    if (isNaN(emissaoNum)) return 0;
    const taxaConversao = 50; // 50 MSC por kg de CO2
    return emissaoNum * taxaConversao;
  };

  // Função para obter ícone do transporte
  const getTransporteIcon = (transporte: string) => {
    switch (transporte) {
      case "Carro":
        return <Car className="h-4 w-4 inline mr-1" />;
      case "Ônibus":
        return <Bus className="h-4 w-4 inline mr-1" />;
      case "Aéreo Convencional":
        return <Plane className="h-4 w-4 inline mr-1" />;
      default:
        return null;
    }
  };

  // Processar check-in
  const handleCheckInSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!checkInForm.cidadeOrigem || !checkInForm.meioTransporte || !checkInForm.distancia) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    const distancia = parseFloat(checkInForm.distancia);
    if (isNaN(distancia) || distancia <= 0) {
      toast({
        title: "Erro",
        description: "A distância deve ser um número válido maior que zero.",
        variant: "destructive"
      });
      return;
    }

    const emissaoCO2 = calcularEmissaoCO2(distancia, checkInForm.meioTransporte);
    const moedas = calcularMoedas(emissaoCO2);

    // Aqui seria feita a chamada à API
    console.log("Check-in registrado:", {
      cliente_id: clientId,
      cidade_origem: checkInForm.cidadeOrigem,
      meio_transporte: checkInForm.meioTransporte,
      distancia: distancia,
      emissao_co2: emissaoCO2,
      moedas: moedas,
      status: "Compensado"
    });

    toast({
      title: "Check-in registrado com sucesso!",
      description: `O Hotel compensou ${emissaoCO2.toFixed(3)} kg de CO₂ e o cliente recebeu ${moedas.toFixed(2)} Moedas.`,
    });

    setCheckInForm({
      cidadeOrigem: "",
      meioTransporte: "",
      distancia: ""
    });
    setIsCheckInModalOpen(false);
  };

  // Calcular totais baseado no tipo
  const totalCheckIns = isHotelaria ? checkIns.length : viagens.length;
  
  const distanciaTotal = isHotelaria 
    ? checkIns.reduce((sum: number, c: any) => {
        const distancia = typeof c.distancia === 'number' ? c.distancia : (parseFloat(String(c.distancia)) || 0);
        return sum + distancia;
      }, 0)
    : viagens.reduce((sum, v) => {
        const distanciaStr = String(v.distancia).replace(" km", "").trim();
        return sum + (parseFloat(distanciaStr) || 0);
      }, 0);
  
  const emissaoTotal = isHotelaria
    ? checkIns.reduce((sum: number, c: any) => {
        const emissao = typeof c.emissao === 'number' ? c.emissao : (parseFloat(String(c.emissao)) || 0);
        return sum + emissao;
      }, 0)
    : viagens.reduce((sum, v) => {
        const emissaoStr = String(v.emissao).replace(" kg", "").replace(",", ".").trim();
        return sum + (parseFloat(emissaoStr) || 0);
      }, 0);

  // Taxa de conversão para cashback (50 MSC por kg de CO2)
  const taxaConversao = 50;

  // Nota: totalMudas e saldoCarteira já foram calculados acima baseados nas viagens
  

  return (
    <div className="container mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800">{client.nomeCompleto}</h1>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
      </div>

      {/* SEÇÃO RETRÁTIL: DADOS CADASTRAIS */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-6 overflow-hidden transition-all duration-200">
        {/* CABEÇALHO CLICÁVEL */}
        <button 
          onClick={() => setShowDadosCadastrais(!showDadosCadastrais)}
          className="w-full px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-gray-500" />
            <h3 className="font-bold text-gray-800">Dados Cadastrais</h3>
          </div>
          
          {/* Ícone que muda de direção */}
          {showDadosCadastrais ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {/* CONTEÚDO RETRÁTIL */}
        {showDadosCadastrais && (
          <div className="p-6">
          {/* Informações Pessoais */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
              <User className="h-4 w-4" />
              Identificação
            </h3>
            
            {/* Linha 1: Nome Completo (Full Width) */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">
                Nome Completo
              </Label>
              <Input
                value={client.nomeCompleto}
                readOnly
                disabled
                className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
              />
            </div>

            {/* Linha 2: CPF | Status da Conta */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-gray-600">
                  CPF
                </Label>
                <Input
                  value={client.cpf}
                  readOnly
                  disabled
                  className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-gray-600">
                  Status da Conta
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={client.status}
                    readOnly
                    disabled
                    className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed flex-1"
                  />
                  <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                    <CheckCircle className="h-3 w-3" />
                    {client.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Linha 3: RG | Data de Nascimento */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-gray-600 flex items-center gap-2">
                  <IdCard className="h-3 w-3" />
                  RG
                </Label>
                <Input
                  value={client.rg}
                  readOnly
                  disabled
                  className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-gray-600 flex items-center gap-2">
                  <Calendar className="h-3 w-3" />
                  Data de Nascimento
                </Label>
                <Input
                  value={client.dataNascimento ? `${client.dataNascimento} (${client.idade} anos)` : ""}
                  readOnly
                  disabled
                  className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Localização & Contato */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Localização & Contato
            </h3>
            
            {/* Contato */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                <Label className="text-sm text-gray-600 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  E-mail
                </Label>
                <Input
                  value={client.email}
                  readOnly
                  disabled
                  className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-gray-600 flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Telefone
                </Label>
                <Input
                  value={client.telefone}
                  readOnly
                  disabled
                  className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                />
              </div>
            </div>

            {/* Endereço Completo */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Home className="h-4 w-4" />
                Endereço de Correspondência
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {/* CEP */}
                <div className="space-y-2 md:col-span-3">
                  <Label className="text-sm text-gray-600">
                    CEP
                  </Label>
                  <Input
                    value={client.cep}
                    readOnly
                    disabled
                    className="bg-white text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>

                {/* Endereço */}
                <div className="space-y-2 md:col-span-6">
                  <Label className="text-sm text-gray-600">
                    Endereço
                  </Label>
                  <Input
                    value={client.endereco}
                    readOnly
                    disabled
                    className="bg-white text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>

                {/* Número */}
                <div className="space-y-2 md:col-span-3">
                  <Label className="text-sm text-gray-600">
                    Número
                  </Label>
                  <Input
                    value={client.numero}
                    readOnly
                    disabled
                    className="bg-white text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>

                {/* Bairro */}
                <div className="space-y-2 md:col-span-4">
                  <Label className="text-sm text-gray-600">
                    Bairro
                  </Label>
                  <Input
                    value={client.bairro}
                    readOnly
                    disabled
                    className="bg-white text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>

                {/* Complemento */}
                <div className="space-y-2 md:col-span-4">
                  <Label className="text-sm text-gray-600">
                    Complemento
                  </Label>
                  <Input
                    value={client.complemento}
                    readOnly
                    disabled
                    className="bg-white text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>

                {/* Cidade */}
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm text-gray-600">
                    Cidade
                  </Label>
                  <Input
                    value={client.cidade}
                    readOnly
                    disabled
                    className="bg-white text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>

                {/* UF */}
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm text-gray-600">
                    UF
                  </Label>
                  <Input
                    value={client.uf}
                    readOnly
                    disabled
                    className="bg-white text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>

                {/* País */}
                <div className="space-y-2 md:col-span-4">
                  <Label className="text-sm text-gray-600">
                    País
                  </Label>
                  <Input
                    value={client.pais}
                    readOnly
                    disabled
                    className="bg-white text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          </div>
          </div>
        )}
      </div>

      {/* Viagens Aéreas ou Histórico de Deslocamento & Compensação */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            {isHotelaria ? (
              <>
                <Hotel className="h-5 w-5 text-[#0e7cb4]" />
                <CardTitle>Histórico de Deslocamento & Compensação</CardTitle>
              </>
            ) : (
              <>
                <Plane className="h-5 w-5 text-[#0e7cb4]" />
                <CardTitle>Viagens Aéreas</CardTitle>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Cards de Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground mb-1">
                  {isHotelaria ? "Check-ins Realizados" : "Total de Viagens"}
                </div>
                <div className="text-3xl font-bold">{totalViagens}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground mb-1">Distância Total (km)</div>
                <div className="text-3xl font-bold">{totalDistancia.toLocaleString('pt-BR')}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground mb-1">
                  {isHotelaria ? "CO₂ Compensado pelo Hotel" : "Emissões KgCO₂e"}
                </div>
                <div className="text-3xl font-bold">{totalEmissao.toLocaleString('pt-BR')}</div>
              </CardContent>
            </Card>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Card className="bg-green-50 border-green-200 cursor-help">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-1">
                        <Sprout className="h-4 w-4 text-green-600" />
                        <div className="text-sm text-muted-foreground">
                          Mudas Plantadas
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-green-600">
                        {totalMudas} {totalMudas === 1 ? 'Muda' : 'Mudas'}
                      </div>
                    </CardContent>
                  </Card>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Árvores nativas plantadas para neutralizar suas emissões de carbono.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="h-4 w-4 text-green-600" />
                  <div className="text-sm text-muted-foreground">
                    Saldo em Carteira
                  </div>
                </div>
                <div className="text-3xl font-bold text-green-600">
                  R$ {saldoCarteira.toFixed(2).replace(".", ",")}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabela - Hotelaria ou Viagens Aéreas */}
          {isHotelaria ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Transporte</TableHead>
                    <TableHead>Distância</TableHead>
                    <TableHead>Emissão</TableHead>
                    <TableHead className="text-right">Crédito Gerado (MSC)</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checkIns.length > 0 ? (
                    checkIns.map((checkIn: any) => (
                      <TableRow key={checkIn.id}>
                        <TableCell>{checkIn.data}</TableCell>
                        <TableCell className="font-medium">{checkIn.origem}</TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            {getTransporteIcon(checkIn.transporte)}
                            <span>{checkIn.transporte}</span>
                          </div>
                        </TableCell>
                        <TableCell><span className="text-muted-foreground">--</span> km</TableCell>
                        <TableCell className="text-[#0e7cb4] font-medium">
                          <span className="text-muted-foreground">--</span> kg
                        </TableCell>
                        <TableCell className="text-right font-semibold text-green-600">
                          <span className="text-muted-foreground">--</span> MSC
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            checkIn.status === "Compensado" 
                              ? "bg-green-100 text-green-800" 
                              : "bg-yellow-100 text-yellow-800"
                          }`}>
                            {checkIn.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Nenhum check-in registrado ainda.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <TooltipProvider>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Hora</TableHead>
                      <TableHead>Rota / Voo</TableHead>
                      <TableHead>Tipo Aeronave</TableHead>
                      <TableHead>Assentos</TableHead>
                      <TableHead>Distância</TableHead>
                      <TableHead>Valor Passagem</TableHead>
                      <TableHead>Emissões KgCO₂e</TableHead>
                      <TableHead>Cashback Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viagens.map((viagem: any, index: number) => {
                      const temNovosCampos = viagem.valorPassagem !== undefined;
                      return (
                        <TableRow key={viagem.id}>
                          <TableCell>{viagem.data}</TableCell>
                          <TableCell>
                            {viagem.horaInicio && viagem.horaFim ? (
                              <span className="text-sm">{viagem.horaInicio} - {viagem.horaFim}</span>
                            ) : viagem.hora ? (
                              <span className="text-sm">{viagem.hora}</span>
                            ) : (
                              <span className="text-muted-foreground text-sm">--</span>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span 
                                    className="border-b border-dotted border-gray-400 cursor-help hover:border-gray-600 transition-colors whitespace-nowrap"
                                    title={gerarTooltipRota(temNovosCampos ? extrairCodigosRota(viagem.rota) : viagem.rota)}
                                  >
                                    {temNovosCampos ? extrairCodigosRota(viagem.rota) : viagem.rota}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <div className="whitespace-pre-line text-sm">
                                    {gerarTooltipRota(temNovosCampos ? extrairCodigosRota(viagem.rota) : viagem.rota)}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell>
                            {viagem.tipoAeronave ? (
                              <span className="text-sm">{viagem.tipoAeronave}</span>
                            ) : (
                              <span className="text-muted-foreground text-sm">--</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {viagem.assentos ? (
                              <span className="text-sm">{viagem.assentos}</span>
                            ) : (
                              <span className="text-muted-foreground text-sm">--</span>
                            )}
                          </TableCell>
                          <TableCell>{viagem.distancia}</TableCell>
                          <TableCell>
                            {temNovosCampos ? (
                              `R$ ${viagem.valorPassagem.toFixed(2).replace(".", ",")}`
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {viagem.emissao ? (
                              <div className="space-y-0.5">
                                <span className="text-[#0e7cb4] font-medium">{viagem.emissao.replace(" kg", "")}</span>
                                {viagem.saf && viagem.safPercentual ? (
                                  <div className="text-xs text-muted-foreground">com SAF {viagem.safPercentual}</div>
                                ) : !viagem.saf ? (
                                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800">
                                    Sem SAF
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {temNovosCampos && viagem.cashbackTotal ? (
                              `R$ ${viagem.cashbackTotal.toFixed(2).replace(".", ",")} (${viagem.taxaCashback}%)`
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      {/* Histórico de Transações do Cliente */}
      <Card className="mt-8 mb-8">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#0e7cb4]" />
            Histórico de Transações do Cliente
          </CardTitle>
          {/* Abas de Filtro */}
          <div className="flex gap-2 mt-4">
            <Button
              variant={filtroExtrato === "todos" ? "default" : "outline"}
              size="sm"
              onClick={() => setFiltroExtrato("todos")}
              className="text-sm"
            >
              Todos
            </Button>
            <Button
              variant={filtroExtrato === "entradas" ? "default" : "outline"}
              size="sm"
              onClick={() => setFiltroExtrato("entradas")}
              className="text-sm"
            >
              Entradas (Ganhos)
            </Button>
            <Button
              variant={filtroExtrato === "saidas" ? "default" : "outline"}
              size="sm"
              onClick={() => setFiltroExtrato("saidas")}
              className="text-sm"
            >
              Saídas (Destinações)
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="table-auto w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Data/Hora</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Origem</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Destino</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Valor</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {transacoesFiltradas.length > 0 ? (
                  transacoesFiltradas.map((transacao, index) => (
                    <tr key={transacao.id || index} className="hover:bg-gray-50 border-b border-gray-100">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transacao.dataHora}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {transacao.origem}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          transacao.tipoTransacao === "entrada"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}>
                          {transacao.tipo}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {transacao.destino}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm font-semibold ${
                        transacao.tipoTransacao === "entrada" ? "text-green-600" : "text-red-600"
                      }`}>
                        {transacao.tipoTransacao === "entrada" ? "+" : "-"} R$ {transacao.valor.toFixed(2).replace(".", ",")}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <FileText className="h-12 w-12 text-gray-300" />
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-600">
                            {filtroExtrato === "entradas" 
                              ? "Nenhuma entrada registrada."
                              : filtroExtrato === "saidas"
                              ? "Nenhuma destinação realizada ainda."
                              : "Nenhuma transação encontrada."}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {filtroExtrato === "saidas" 
                              ? "As transações aparecerão aqui após o cliente confirmar destinações."
                              : ""}
                          </p>
                      </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
                    </div>
                  </CardContent>
                </Card>

      {/* Central de Saldo & Cartão */}
      <Card>
        <CardHeader>
          <CardTitle>Central de Saldo & Cartão</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Aba: Cartão Moeda Social Carbono */}
          <div className="flex flex-col md:flex-row items-start gap-8">
                {/* Coluna Esquerda: Visual do Cartão */}
            <div className="relative w-fit select-none flex-grow-0">
                    <img
                      src="/images/cartão moeda.png"
                alt="Cartão Moeda Social Carbono Banclima"
                className="block rounded-xl shadow-2xl max-w-[380px]"
                      onError={(e) => {
                  // Tenta o caminho alternativo com hífen se o com espaço não funcionar
                  const target = e.target as HTMLImageElement;
                  if (target.src.includes('cartão moeda')) {
                    target.src = "/images/cartão-moeda.png";
                  } else if (target.src.includes('cartão-moeda')) {
                    target.src = "/images/cartãomoeda.png";
                  } else {
                    // Fallback para placeholder cinza se nenhuma imagem carregar
                    target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='380' height='240'%3E%3Crect fill='%23cccccc' width='380' height='240' rx='12'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999' font-size='16'%3ECartão Banclima%3C/text%3E%3C/svg%3E";
                  }
                      }}
                    />
              {/* Overlay: Nome do Cliente - Posicionado no meio-esquerda do cartão */}
              <div className="absolute top-[45%] left-6 text-white font-bold text-xl tracking-wider drop-shadow-md z-10">
                {client.nomeCompleto.toUpperCase()}
                    </div>
              
              {/* Overlay: Número do Cartão - Logo abaixo da frase "MOEDA SOCIAL CARBONO" */}
              <div className="absolute bottom-2 left-6 text-white font-mono text-xl tracking-[0.2em] drop-shadow-md z-10">
                •••• •••• •••• 1234
                  </div>
                </div>

                {/* Coluna Direita: Status e Ações */}
            <div className="space-y-6 flex-1">
                  {/* Status */}
                  <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Status</Label>
                    <span className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-green-100 text-green-800">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Ativo
                    </span>
                  </div>

                  {/* Saldo Vinculado */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Saldo Vinculado</Label>
                    <div className="text-3xl font-bold text-green-600">
                  R$ {saldoVinculado.toFixed(2).replace(".", ",")}
                    </div>
              </div>
              
              {/* Saldo Líquido */}
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Saldo Líquido</Label>
                <div className="text-2xl font-bold text-blue-600">
                  R$ {saldoLiquido.toFixed(2).replace(".", ",")}
                </div>
                  </div>

                  {/* Botões de Ação */}
                  <div className="space-y-3">
                    <Button variant="outline" className="w-full justify-start" size="lg">
                      <Lock className="h-4 w-4 mr-2" />
                      Bloquear Temporariamente
                    </Button>
                  </div>

                  {/* Aviso */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                  <strong>Aviso:</strong> Este cartão utiliza seu saldo MSC automaticamente.
                    </p>
                  </div>
                </div>
              </div>
        </CardContent>
      </Card>

      {/* Status de Repasse Social Obrigatório (Regra dos 10 Dias) */}
      <Card>
        <CardHeader>
          <CardTitle>Status de Repasse Social Obrigatório (Regra dos 10 Dias)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Card: Status do Repasse (Dinâmico baseado no mockDB) */}
            {statusRepasse === 'pendente' ? (
              <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-yellow-600" />
                    <span className="font-semibold text-gray-900">
                      Voo SDU-GRU (20/12/2025)
                    </span>
                  </div>
                  <span className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-yellow-100 text-yellow-800">
                    Pendente
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Aguardando confirmação de destinação pelo cliente.
                </div>
              </div>
            ) : (
              <div className={`border ${statusRepasse === 'concluido' ? 'border-green-200 bg-green-50/50' : 'border-blue-200 bg-blue-50/50'} rounded-lg p-4`}>
                {/* Cabeçalho */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {statusRepasse === 'concluido' ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <Clock className="h-5 w-5 text-blue-600" />
                    )}
                    <span className="font-semibold text-gray-900">
                      Voo SDU-GRU (20/12/2025)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                      statusRepasse === 'concluido' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {statusRepasse === 'concluido' ? 'Concluído' : 'Parcialmente Compensado'}
                    </span>
                    <Button variant="outline" size="sm" className="gap-2">
                      <FileText className="h-4 w-4" />
                      Gerar Comprovante PDF
                    </Button>
                  </div>
                </div>

                {/* Sub-cabeçalho: Metadados da Transação */}
                {dadosMockDB && dadosMockDB.resumoDestinacoes && dadosMockDB.resumoDestinacoes.length > 0 && (
                  <div className="mb-4 space-y-1">
                    <div className="text-sm text-gray-500">
                      <span className="font-medium">Cashback Base:</span> R$ 20,00
                    </div>
                    {dadosMockDB.transacoes && dadosMockDB.transacoes.length > 0 && (
                      <div className="text-sm text-gray-500">
                        <span className="font-medium">Data da Destinação:</span> {dadosMockDB.transacoes[0].dataHora}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Corpo: Grid de Destinação */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Coluna 1: Apoio a Mudas */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Sprout className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-sm text-gray-700">Apoio a Mudas</span>
                    </div>
                    <div className="flex items-baseline gap-2 mb-1">
                      <div className="text-2xl font-bold text-green-600">
                        R$ {fundoMudas.toFixed(2).replace(".", ",")}
                      </div>
                      <div className="text-sm font-medium text-gray-600">
                        ({((fundoMudas / 20.00) * 100).toFixed(2)}%)
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mb-3">
                      Contribuição do Cliente (Meta: R$ 6,00)
                    </div>
                    
                    {/* Barra de Progresso */}
                    {fundoMudas > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-700">Progresso para 1ª Muda</span>
                          <span className="text-xs text-gray-500">
                            {Math.min(100, Math.round((fundoMudas / 6.00) * 100))}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full" 
                            style={{ width: `${Math.min(100, Math.round((fundoMudas / 6.00) * 100))}%` }}
                          ></div>
                        </div>
                        {fundoMudas < 6.00 && (
                          <div className="mb-2">
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                              <Info className="h-3 w-3" />
                              Incompleta: Faltam R$ {(6.00 - fundoMudas).toFixed(2).replace(".", ",")} para fechar a unidade.
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Informações do projeto com valores agrupados */}
                    {(() => {
                      // Agrupa transações de "Apoio a Mudas" por projeto e soma os valores
                      const transacoesMudas = transacoes.filter((t: any) => 
                        (t.tipo === 'Apoio a Mudas' || t.tipo?.includes('Mudas')) && 
                        t.tipoTransacao === 'saida'
                      );
                      
                      // Agrupa por projeto (extrai do destino: "Região - Projeto" ou "Apoio a Mudas - Região - Projeto")
                      const projetosAgrupados: Record<string, { regiao: string; projeto: string; valorTotal: number }> = {};
                      
                      transacoesMudas.forEach((trans: any) => {
                        const partes = trans.destino.split(' - ');
                        let regiao = '';
                        let projeto = '';
                        
                        if (partes.length >= 3) {
                          // Formato: "Apoio a Mudas - Região - Projeto"
                          regiao = partes[1];
                          projeto = partes[2];
                        } else if (partes.length === 2) {
                          // Formato: "Região - Projeto"
                          regiao = partes[0];
                          projeto = partes[1];
                        } else {
                          projeto = trans.destino;
                        }
                        
                        const chave = `${regiao}-${projeto}`;
                        if (!projetosAgrupados[chave]) {
                          projetosAgrupados[chave] = { regiao, projeto, valorTotal: 0 };
                        }
                        projetosAgrupados[chave].valorTotal += Math.abs(trans.valor || 0);
                      });
                      
                      const projetosLista = Object.values(projetosAgrupados);
                      
                      return projetosLista.length > 0 ? (
                        <div className="mt-3 space-y-1">
                          {projetosLista.map((proj, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                              <div className="flex items-center gap-2">
                                {proj.regiao && (
                                  <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase font-bold">
                                    {proj.regiao}
                                  </span>
                                )}
                                <span className="text-sm text-gray-700">
                                  {proj.projeto}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-green-700">
                                R$ {proj.valorTotal.toFixed(2).replace('.', ',')}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>

                  {/* Coluna 2: Projetos Sociais */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Wallet className="h-4 w-4 text-orange-600" />
                      <span className="font-medium text-sm text-gray-700">Projetos Sociais</span>
                    </div>
                    <div className="flex items-baseline gap-2 mb-2">
                      <div className="text-2xl font-bold text-orange-600">
                        R$ {projetosSociais.toFixed(2).replace(".", ",")}
                      </div>
                      <div className="text-sm font-medium text-gray-600">
                        ({((projetosSociais / 20.00) * 100).toFixed(2)}%)
                      </div>
                    </div>

                    {/* Informações do projeto com valores agrupados */}
                    {(() => {
                      // Agrupa transações de "Projetos Sociais" por projeto e soma os valores
                      const transacoesSociais = transacoes.filter((t: any) => 
                        t.tipo === 'Projetos Sociais' && 
                        t.tipoTransacao === 'saida'
                      );
                      
                      // Agrupa por projeto (extrai do destino: "Região - Projeto" ou "Projetos Sociais - Região - Projeto")
                      const projetosAgrupados: Record<string, { regiao: string; projeto: string; valorTotal: number }> = {};
                      
                      transacoesSociais.forEach((trans: any) => {
                        const partes = trans.destino.split(' - ');
                        let regiao = '';
                        let projeto = '';
                        
                        if (partes.length >= 3) {
                          // Formato: "Projetos Sociais - Região - Projeto"
                          regiao = partes[1];
                          projeto = partes[2];
                        } else if (partes.length === 2) {
                          // Formato: "Região - Projeto"
                          regiao = partes[0];
                          projeto = partes[1];
                        } else {
                          projeto = trans.destino;
                        }
                        
                        const chave = `${regiao}-${projeto}`;
                        if (!projetosAgrupados[chave]) {
                          projetosAgrupados[chave] = { regiao, projeto, valorTotal: 0 };
                        }
                        projetosAgrupados[chave].valorTotal += Math.abs(trans.valor || 0);
                      });
                      
                      const projetosLista = Object.values(projetosAgrupados);
                      
                      return projetosLista.length > 0 ? (
                        <div className="mt-3 space-y-1">
                          {projetosLista.map((proj, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                              <div className="flex items-center gap-2">
                                {proj.regiao && (
                                  <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded uppercase font-bold">
                                    {proj.regiao}
                                  </span>
                                )}
                                <span className="text-sm text-gray-700">
                                  {proj.projeto}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-orange-700">
                                R$ {proj.valorTotal.toFixed(2).replace('.', ',')}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Rodapé */}
                <div className="text-sm text-gray-600 bg-white rounded-lg p-3 border border-gray-200">
                  Saldo líquido creditado ao cliente: <span className="font-semibold">
                    R$ {saldoLiquido.toFixed(2).replace(".", ",")} ({((saldoLiquido / 20.00) * 100).toFixed(0)}%)
                  </span>
                </div>
              </div>
            )}

          </div>
        </CardContent>
      </Card>

      {/* Modal de Registro de Check-in */}
      <Dialog open={isCheckInModalOpen} onOpenChange={setIsCheckInModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Registrar Chegada (Check-in)</DialogTitle>
            <DialogDescription>
              Registre o check-in do hóspede e calcule a compensação de carbono do deslocamento.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCheckInSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="cidadeOrigem">Cidade de Origem *</Label>
                <Input
                  id="cidadeOrigem"
                  placeholder="Ex: São Paulo, SP"
                  value={checkInForm.cidadeOrigem}
                  onChange={(e) => setCheckInForm({...checkInForm, cidadeOrigem: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="meioTransporte">Meio de Transporte *</Label>
                <Select 
                  value={checkInForm.meioTransporte} 
                  onValueChange={(value) => setCheckInForm({...checkInForm, meioTransporte: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o meio de transporte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Carro">🚗 Carro</SelectItem>
                    <SelectItem value="Ônibus">🚌 Ônibus</SelectItem>
                    <SelectItem value="Aéreo Convencional">✈️ Aéreo Convencional</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="distancia">Distância Estimada (km) *</Label>
                <Input
                  id="distancia"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="Ex: 350.5"
                  value={checkInForm.distancia}
                  onChange={(e) => setCheckInForm({...checkInForm, distancia: e.target.value})}
                  required
                />
              </div>

              {checkInForm.distancia && checkInForm.meioTransporte && parseFloat(checkInForm.distancia) > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-sm mb-2">Resumo da Compensação:</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Emissão estimada:</span>
                      <span className="font-medium">
                        {calcularEmissaoCO2(parseFloat(checkInForm.distancia), checkInForm.meioTransporte).toFixed(3)} kg CO₂
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Moedas a serem creditadas:</span>
                      <span className="font-medium text-[#0e7cb4]">
                        {calcularMoedas(calcularEmissaoCO2(parseFloat(checkInForm.distancia), checkInForm.meioTransporte)).toFixed(2)} MSC
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCheckInModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]">
                <CheckCircle className="mr-2 h-4 w-4" />
                Registrar Check-in
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetail;
